import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Badge,
	Button,
} from "@/components/primitives";
import {
	Target,
	ArrowRight,
	CheckCircle,
	PlayCircle,
	Clock,
	TrendingUp,
} from "lucide-react";

interface CorrectiveAction {
	id: string;
	reflection_id: string;
	title: string;
	description: string;
	priority: number;
	status: "not_started" | "in_progress" | "completed";
	created_at: string;
	updated_at: string;
	reflection?: {
		id: string;
		title: string;
		description: string;
	};
}

interface TopPriorityActionProps {
	variant?: "homepage" | "checkin";
	className?: string;
}

export function TopPriorityAction({
	variant = "homepage",
	className = "",
}: TopPriorityActionProps) {
	const { user } = useAuth();
	const [topAction, setTopAction] = useState<CorrectiveAction | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (user) {
			fetchTopPriorityAction();
		}
	}, [user]);

	const fetchTopPriorityAction = async () => {
		if (!user) return;

		try {
			const { data, error } = await supabase
				.from("corrective_actions")
				.select(
					`
          *,
          reflections!corrective_actions_reflection_id_fkey(
            id,
            title,
            description
          )
        `,
				)
				.eq("reflections.user_id", user.id)
				.neq("status", "completed")
				.order("priority", { ascending: true })
				.limit(1)
				.maybeSingle();

			if (error) {
				throw error;
			}

			if (data) {
				setTopAction({
					...data,
					reflection: data.reflections,
				});
			}
		} catch (error) {
			console.error("Error fetching top priority action:", error);
		} finally {
			setLoading(false);
		}
	};

	const getStatusIcon = (status: CorrectiveAction["status"]) => {
		switch (status) {
			case "completed":
				return <CheckCircle className="h-4 w-4 text-green-600" />;
			case "in_progress":
				return <PlayCircle className="h-4 w-4 text-blue-600" />;
			default:
				return <Clock className="h-4 w-4 text-gray-400" />;
		}
	};

	const getStatusColor = (status: CorrectiveAction["status"]) => {
		switch (status) {
			case "completed":
				return "bg-green-50 text-green-700 border-green-200";
			case "in_progress":
				return "bg-blue-50 text-blue-700 border-blue-200";
			default:
				return "bg-gray-50 text-gray-700 border-gray-200";
		}
	};

	if (loading) {
		return (
			<Card className={className}>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2 text-base">
						<Target className="h-4 w-4" />
						<span>Current Priority</span>
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex items-center justify-center py-4">
						<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
					</div>
				</CardContent>
			</Card>
		);
	}

	if (!topAction) {
		return (
			<Card className={className}>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2 text-base">
						<Target className="h-4 w-4" />
						<span>Current Priority</span>
					</CardTitle>
					<CardDescription>Your highest priority action item</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="text-center py-4">
						<Target className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
						<p className="text-sm text-muted-foreground mb-3">
							No active corrective actions
						</p>
						<Button variant="outline" size="sm" asChild>
							<Link to="/reflection/new">Create Reflection</Link>
						</Button>
					</div>
				</CardContent>
			</Card>
		);
	}

	if (variant === "checkin") {
		return (
			<Card className={className}>
				<CardHeader className="pb-3">
					<CardTitle className="flex items-center space-x-2 text-base">
						<Target className="h-4 w-4 text-orange-600" />
						<span>Today's Priority</span>
					</CardTitle>
					<CardDescription>
						Your highest priority action to focus on
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-3">
					<div className="flex items-center space-x-2">
						<Badge variant="outline" className="text-xs">
							#1 Priority
						</Badge>
						<Badge className={getStatusColor(topAction.status)}>
							{getStatusIcon(topAction.status)}
							<span className="ml-1 capitalize">
								{topAction.status.replace("_", " ")}
							</span>
						</Badge>
					</div>

					<div>
						<h4 className="font-medium text-sm mb-1">{topAction.title}</h4>
						<p className="text-xs text-muted-foreground">
							{topAction.description.slice(0, 100)}
							{topAction.description.length > 100 ? "..." : ""}
						</p>
					</div>

					<div className="bg-muted p-2 rounded text-xs">
						<span className="text-muted-foreground">From:</span>{" "}
						<span className="font-medium">{topAction.reflection?.title}</span>
					</div>

					<Button variant="outline" size="sm" className="w-full" asChild>
						<Link to="/corrective-actions">
							View All Actions
							<ArrowRight className="h-3 w-3 ml-1" />
						</Link>
					</Button>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card className={className}>
			<CardHeader>
				<div className="flex items-center justify-between">
					<CardTitle className="flex items-center space-x-2">
						<Target className="h-5 w-5 text-orange-600" />
						<span>Current Priority</span>
					</CardTitle>
					<Badge variant="outline" className="text-xs">
						#1 Priority
					</Badge>
				</div>
				<CardDescription>
					Your highest priority action to focus on today
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="flex items-center space-x-2">
					<Badge className={getStatusColor(topAction.status)}>
						{getStatusIcon(topAction.status)}
						<span className="ml-1 capitalize">
							{topAction.status.replace("_", " ")}
						</span>
					</Badge>
				</div>

				<div>
					<h3 className="font-semibold text-lg mb-2">{topAction.title}</h3>
					<p className="text-muted-foreground text-sm mb-3">
						{topAction.description}
					</p>
				</div>

				<div className="bg-muted p-3 rounded-lg">
					<div className="flex items-center space-x-2 text-sm">
						<TrendingUp className="h-4 w-4 text-muted-foreground" />
						<span className="text-muted-foreground">From reflection:</span>
						<span className="font-medium">{topAction.reflection?.title}</span>
					</div>
				</div>

				<div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
					<Button
						variant="outline"
						size="sm"
						asChild
						className="w-full sm:w-auto"
					>
						<Link to={`/reflection/${topAction.reflection_id}`}>
							View Context
						</Link>
					</Button>
					<Button
						variant="outline"
						size="sm"
						asChild
						className="w-full sm:w-auto"
					>
						<Link to="/corrective-actions">
							Manage Actions
							<ArrowRight className="h-4 w-4 ml-1" />
						</Link>
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}
